import { Loader2, MessageSquare, Phone, TriangleAlert } from "lucide-react"
import { useCallback, useEffect, useState } from "react"

import { TextSimulator } from "@/components/agents/setup/lead-responder/text-simulator"
import { LiveTestCall } from "@/components/agents/setup/live-test-call"
import {
  PhoneNumberPicker,
  numberChoiceComplete,
  useNumberChoices,
} from "@/components/agents/setup/phone-number-picker"
import { StepHeader, WizardLayout } from "@/components/agents/setup/shell"
import { FormField } from "@/components/form-field"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"
import { launchPreviewAgentPath } from "@/routes"

import { STEP_TITLE, type WizardStep } from "./steps"
import type { LeadResponderSetupStepProps } from "./types"

// IMP-360 rework (Chris's design): the static "here's what the agent would
// say" preview read as canned, so it's replaced with two hands-on tests in
// tabs — a browser-mic live call (the receptionist's LiveTestCall panel,
// which works pre-launch) and a practice text thread against the real agent.
// The green "ready" banner and the amber SMS warning are gone; readiness
// errors still render and gate Launch, each linking to the step that fixes it.

interface ActivateStepProps extends LeadResponderSetupStepProps {
  // The agent's uid — used by the test call, text simulator, and readiness
  // fetch. Always supplied by the wizard's dedicated Activate branch; optional
  // only so the shared `{...common}` fallback render still typechecks
  // (matches Sources / Compliance steps).
  agentUid?: string
  // IMP-360: jump back to a wizard step a readiness issue points at.
  onGoToStep?: (step: WizardStep) => void
  // IMP-345: the launch request is in flight — show a loader and lock the footer.
  launching?: boolean
  // IMP-538: bump to re-fetch the number choices (after a failed Launch).
  numbersRefreshKey?: number
}

interface ReadinessIssue {
  level: "error" | "warning"
  step: WizardStep
  message: string
}

interface LaunchPreview {
  ready: boolean
  issues: ReadinessIssue[]
}

export function ActivateStep({
  stepNumber,
  totalSteps,
  data,
  onUpdate,
  onNext,
  onBack,
  agentUid,
  onGoToStep,
  launching = false,
  numbersRefreshKey = 0,
  textingEnabled,
}: ActivateStepProps) {
  const { choices: numberChoices, retry: retryNumbers } = useNumberChoices(
    agentUid,
    data.sms_area_code,
    numbersRefreshKey,
  )
  const [result, setResult] = useState<LaunchPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const checkReadiness = useCallback(async () => {
    if (!agentUid) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(launchPreviewAgentPath({ id: agentUid }), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!response.ok) throw new Error("Couldn't check readiness.")
      setResult((await response.json()) as LaunchPreview)
    } catch {
      setError("Couldn't check readiness. Please try again.")
    } finally {
      setLoading(false)
    }
  }, [agentUid])

  useEffect(() => {
    void checkReadiness()
  }, [checkReadiness])

  const errors = result?.issues.filter((i) => i.level === "error") ?? []
  // IMP-461: the consent attestation lives on this step now (the standalone
  // Compliance step is gone). It's checked locally and persisted at Launch, so
  // the server-side "consent missing" readiness error (step-activate) is
  // satisfied by the local checkbox; everything else must still be fixed.
  const outstanding = errors.filter((i) => i.step !== "step-activate")
  const canLaunch =
    data.consent_attested &&
    !loading &&
    error == null &&
    result != null &&
    outstanding.length === 0 &&
    // IMP-538: once numbers are listed, one must be picked.
    numberChoiceComplete(numberChoices, data.selected_phone_number)

  return (
    <WizardLayout
      footer={
        <>
          <Button variant="outline" onClick={onBack} disabled={launching}>
            Back
          </Button>
          <Button onClick={onNext} disabled={!canLaunch || launching}>
            {launching && <Loader2 className="size-4 animate-spin" />}
            {launching ? "Launching…" : "Launch"}
          </Button>
        </>
      }
    >
      <StepHeader
        eyebrow={`Step ${stepNumber} of ${totalSteps}`}
        title="Test and go live"
        description={
          textingEnabled
            ? "Talk to me in your browser or text me below to hear how I respond on a call. When you launch, I get my own number and start calling new leads."
            : "Talk to me in your browser to hear how I respond on a call. When you launch, I get my own number and start calling new leads."
        }
      />

      {/* IMP-355/461: the auditable consent attestation gates Launch. */}
      <label className="bg-muted/60 flex items-start gap-3 rounded-lg border p-4">
        <Checkbox
          checked={data.consent_attested}
          onCheckedChange={(v) => onUpdate({ consent_attested: v === true })}
          className="mt-0.5"
        />
        <span className="min-w-0">
          <span className="block text-sm font-medium">
            These leads have agreed to be contacted
          </span>
          <span className="text-muted-foreground mt-0.5 block text-sm">
            I only call leads from your sources who consented to hear from your
            business.
          </span>
        </span>
      </label>

      {/* Preferred area code: the number is provisioned at launch (IMP-391). */}
      <FormField label="Preferred area code" htmlFor="area_code">
        <Input
          id="area_code"
          inputMode="numeric"
          maxLength={3}
          value={data.sms_area_code}
          onChange={(e) => {
            const code = e.target.value.replace(/\D/g, "").slice(0, 3)
            onUpdate({
              sms_area_code: code,
              // A number picked for another area code no longer applies.
              ...(code !== data.sms_area_code && { selected_phone_number: "" }),
            })
          }}
          placeholder="e.g. 415"
          className="w-full"
        />
        {/* IMP-538: pick the actual number, rather than being assigned one. */}
        <div className="mt-3">
          <PhoneNumberPicker
            areaCode={data.sms_area_code}
            choices={numberChoices}
            selected={data.selected_phone_number}
            onSelect={(phone) => onUpdate({ selected_phone_number: phone })}
            onRetry={retryNumbers}
          />
        </div>
      </FormField>

      {/* Readiness errors, each linking to the step that fixes it. Warnings
          (SMS registration pending) no longer render here per the design
          rework — they don't block launch. */}
      {error ? (
        <p className="text-destructive text-sm">{error}</p>
      ) : (
        outstanding.map((issue, idx) => (
          <IssueRow key={idx} issue={issue} onGoToStep={onGoToStep} />
        ))
      )}
      {!loading && outstanding.length > 0 && (
        <p className="text-muted-foreground text-sm">
          Fix the items above before launching.
        </p>
      )}

      {/* IMP-423: while texting is pulled from self-serve the agent is
          call-only, so the test is a single live call (matching the
          Receptionist), no call/text tabs. Flip the gate to restore the
          "Text your agent" tab. */}
      {textingEnabled ? (
        <Tabs defaultValue="call">
          <TabsList variant="line">
            <TabsTrigger value="call">
              <Phone />
              Live test call
            </TabsTrigger>
            <TabsTrigger value="text">
              <MessageSquare />
              Text your agent
            </TabsTrigger>
          </TabsList>
          <TabsContent value="call" className="mt-4">
            {agentUid && (
              <LiveTestCall
                agentUid={agentUid}
                // IMP-532: the test emulates an outbound follow-up — the agent
                // waits for the tester to answer before delivering its opener.
                idleDescription="You play the lead answering their phone — say “Hello?” and I'll take it from there."
                speakerLabel="Assistant"
                waitingMessage="Connected — answer like you've just picked up the phone."
              />
            )}
          </TabsContent>
          <TabsContent value="text" className="mt-4">
            {agentUid && <TextSimulator agentUid={agentUid} />}
          </TabsContent>
        </Tabs>
      ) : (
        agentUid && (
          <LiveTestCall
            agentUid={agentUid}
            title="Live test call"
            // IMP-532: the test emulates an outbound follow-up — the agent
            // waits for the tester to answer before delivering its opener.
            idleDescription="You play the lead answering their phone — say “Hello?” and I'll take it from there."
            speakerLabel="Assistant"
            waitingMessage="Connected — answer like you've just picked up the phone."
          />
        )
      )}
    </WizardLayout>
  )
}

function IssueRow({
  issue,
  onGoToStep,
}: {
  issue: ReadinessIssue
  onGoToStep?: (step: WizardStep) => void
}) {
  const isError = issue.level === "error"
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border p-3 text-sm",
        isError
          ? "border-destructive/40 bg-destructive/5"
          : "border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40",
      )}
    >
      <TriangleAlert
        className={cn(
          "mt-0.5 size-4 shrink-0",
          isError ? "text-destructive" : "text-amber-600",
        )}
      />
      <div className="min-w-0 flex-1">
        <p>{issue.message}</p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-auto shrink-0 px-2 py-1"
        onClick={() => onGoToStep?.(issue.step)}
      >
        Fix in {STEP_TITLE[issue.step]}
      </Button>
    </div>
  )
}
